import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { B2BInvoice, B2bInvoiceService } from '../../../services/b2b-invoice.service';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-b2b-invoice-detail',
  templateUrl: './b2b-invoice-detail.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css']
})
export class B2bInvoiceDetailComponent implements OnInit {
  contractId: number | null = null;
  invoiceId: number | null = null;
  contractNumber = '';
  contractName = '';
  companyName = '';
  customerName = '';
  invoice: B2BInvoice | null = null;
  loading = false;
  acting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: B2bInvoiceService,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(p => {
      this.contractId = Number(p.get('id')) || null;
      this.invoiceId = Number(p.get('invoiceId')) || null;
      if (this.contractId) this.loadContract();
      if (this.invoiceId) this.loadInvoice();
    });
  }

  get canIssue(): boolean {
    return this.invoice?.status === 'Draft';
  }

  get canOpenCr(): boolean {
    return !!(this.invoice?.accountsReceivableId && this.invoice.status !== 'Draft' && this.invoice.status !== 'Cancelled');
  }

  loadContract(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c: any) => {
        this.contractNumber = c.contractNumber || c.ContractNumber || '';
        this.contractName = c.contractName || c.ContractName || '';
        this.companyName = c.companyName || c.CompanyName || '';
        this.customerName = c.customerName || c.CustomerName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  get contractDisplayName(): string {
    return this.companyName
      || this.customerName
      || this.contractName
      || this.invoice?.companyName
      || this.invoice?.contractName
      || this.contractNumber
      || '';
  }

  loadInvoice(): void {
    if (!this.invoiceId) return;
    this.loading = true;
    this.invoiceService.getById(this.invoiceId).subscribe({
      next: (inv) => {
        this.invoice = this.normalize(inv);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  back(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'faturas']);
      return;
    }
    this.router.navigate(['/b2b/billing-closing']);
  }

  issue(): void {
    if (!this.invoice || !this.canIssue) return;
    this.confirmationService.confirm({
      title: 'Emitir fatura',
      message: `Emitir ${this.invoice.invoiceNumber} e criar apenas o título no Contas a Receber (sem cobrança Asaas)? O espelho fica disponível para o cliente no portal.`,
      confirmText: 'Emitir'
    }).subscribe({
      next: (ok) => {
        if (!ok) return;
        this.acting = true;
        this.invoiceService.issue(this.invoice!.id).subscribe({
          next: (issued) => {
            this.acting = false;
            this.invoice = this.normalize(issued);
            this.errorService.showSuccessMessage(
              'Fatura emitida. Contas a Receber criado — espelho liberado (Asaas desligado neste fluxo).'
            );
          },
          error: (err) => {
            this.acting = false;
            this.errorService.handleHttpError(err);
          }
        });
      }
    });
  }

  openAccountsReceivable(): void {
    if (!this.invoice?.accountsReceivableId) {
      this.errorService.showError('Esta fatura ainda não possui Contas a Receber. Emita a fatura primeiro.');
      return;
    }
    this.router.navigate(['/financial/accounts-receivable', this.invoice.accountsReceivableId, 'view']);
  }

  export(format: 'xlsx' | 'pdf'): void {
    if (!this.invoice) return;
    this.invoiceService.exportInvoice(this.invoice.id, format).subscribe({
      next: (blob) => {
        const ext = format === 'pdf' ? 'pdf' : 'csv';
        saveAs(blob, `${this.invoice!.invoiceNumber}.${ext}`);
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'Draft': return 'Rascunho';
      case 'Issued': return 'Emitida';
      case 'Paid': return 'Paga';
      case 'Cancelled': return 'Cancelada';
      default: return status;
    }
  }

  formatMoney(v: any): string {
    const n = Number(v);
    if (isNaN(n)) return '—';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatLineDate(value?: string | null): string {
    if (!value) return '—';
    const raw = value.includes('T') ? value.slice(0, 10) : value.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}/.test(raw)) return value;
    const [y, m, d] = raw.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('pt-BR');
  }

  private normalize(raw: any): B2BInvoice {
    return {
      id: raw.id ?? raw.Id,
      b2BContractId: raw.b2BContractId ?? raw.B2BContractId,
      contractNumber: raw.contractNumber ?? raw.ContractNumber,
      contractName: raw.contractName ?? raw.ContractName,
      companyName: raw.companyName ?? raw.CompanyName,
      customerId: raw.customerId ?? raw.CustomerId,
      competenceYear: raw.competenceYear ?? raw.CompetenceYear,
      competenceMonth: raw.competenceMonth ?? raw.CompetenceMonth,
      category: raw.category ?? raw.Category,
      categoryLabel: raw.categoryLabel ?? raw.CategoryLabel,
      status: raw.status ?? raw.Status,
      invoiceNumber: raw.invoiceNumber ?? raw.InvoiceNumber,
      totalAmount: Number(raw.totalAmount ?? raw.TotalAmount ?? 0),
      dueDate: raw.dueDate ?? raw.DueDate,
      issuedAt: raw.issuedAt ?? raw.IssuedAt,
      paidAt: raw.paidAt ?? raw.PaidAt,
      accountsReceivableId: raw.accountsReceivableId ?? raw.AccountsReceivableId,
      notes: raw.notes ?? raw.Notes,
      createdAt: raw.createdAt ?? raw.CreatedAt,
      items: (raw.items ?? raw.Items ?? []).map((i: any, idx: number) => ({
        id: i.id ?? i.Id ?? idx,
        plate: i.plate ?? i.Plate,
        description: i.description ?? i.Description,
        reference: i.reference ?? i.Reference,
        days: i.days ?? i.Days,
        unitAmount: i.unitAmount ?? i.UnitAmount,
        amount: Number(i.amount ?? i.Amount ?? 0),
        sourceType: i.sourceType ?? i.SourceType,
        sourceId: i.sourceId ?? i.SourceId,
        periodStart: i.periodStart ?? i.PeriodStart,
        periodEnd: i.periodEnd ?? i.PeriodEnd
      }))
    };
  }
}
